import React, { FC, useContext, useMemo } from 'react';

import { useFormikContext } from 'formik';

// Components
import ComponentTypesIconWrapper from '../../../../../../components/componentTypesIcon/ComponentTypesIconWrapper';
import useVariantsByComponentIdQuery from 'hooks/components/useVariantsByComponentIdQuery';
// Constants
import styled from 'styled-components';

import { EditorContext } from 'context/EditorContext';

// Hooks
import useComponentTypesQuery from '../../../../../../hooks/component-types/useComponentTypesQuery';
import { getDuplicateComponentMutualLabelMap } from 'hooks/formulas/helper';
import useVariantsByMessageContentIdQuery from 'hooks/variants/useVariantsByMessageContentIdQuery';

// Models
import {
    ImageVariantElement,
    ImageVariantEmailElement,
    VariantElement,
} from '../../../../../../models/editor.model';
import { FormulaTestMessageFormValues } from 'models/formula.model';
import { TestFormulaVariantData, VariantWithLabel } from 'models/variant.model';

import TestFormulaElementField from '../TestFormulaElementField';

interface TestFormulaVariantBlockProps {
    component: VariantElement | ImageVariantElement | ImageVariantEmailElement;
}

const TestFormulaVariantBlock: FC<TestFormulaVariantBlockProps> = ({
    component,
}) => {
    const { values } = useFormikContext<FormulaTestMessageFormValues>();
    const { editorDetails } = useContext(EditorContext);

    const { data: componentTypes } = useComponentTypesQuery();
    const { data: messageContentVariants = [] } =
        useVariantsByMessageContentIdQuery(+editorDetails.formula?.id!);

    const duplicateComponentTypeWithMutualLabel =
        getDuplicateComponentMutualLabelMap(messageContentVariants || []);

    const componentType = useMemo(() => {
        return componentTypes?.list.find(
            (ct) => ct.data_key === component.variantType,
        );
    }, [componentTypes, component.variantType]);

    const { data: alternateVariants = [] } = useVariantsByComponentIdQuery(
        component.id,
    );

    const selectedVariant = useMemo<VariantWithLabel | undefined>(() => {
        const alternateId = values?.filters?.alternates?.[component.id];
        if (alternateId) {
            return alternateVariants.find((v) => v.id === alternateId);
        }
        return undefined;
    }, [alternateVariants, values, component]);

    const varinatTableData = useMemo<TestFormulaVariantData[]>(() => {
        const duplicateMap = duplicateComponentTypeWithMutualLabel || {};
        const duplicateComponentTypeIds = Object.keys(duplicateMap);
        const componentAttachedLabel = values.componentAttachedLabel || {};

        const isFormulaLocked =
            editorDetails.formula?.is_labeled_matching_enabled;

        // If label-matching is disabled, allow everything
        if (!isFormulaLocked) {
            return alternateVariants.map((v) => ({
                ...v,
                isDisabled: false,
            }));
        }

        return alternateVariants.map((variant) => {
            const componentTypeId = variant.component_type_id;
            const labelId = variant.label?.id;

            // If this component type is NOT duplicated, or none of its
            // alternates are labelled, there are no label restrictions
            if (!duplicateComponentTypeIds.includes(componentTypeId)) {
                return { ...variant, isDisabled: false };
            }

            // Get mutual labels for this duplicated component type
            const mutualLabels = duplicateMap[componentTypeId];

            // Get labels selected by OTHER component instances
            // of the same component type
            const selectedLabelsForType = Object.entries(componentAttachedLabel)
                .filter(
                    ([instanceId, value]) =>
                        value.componentTypeId === componentTypeId &&
                        instanceId !== variant.component_id,
                )
                .map(([, value]) => value.labelId)
                .filter((l): l is number => l != null);

            const uniqueSelected = [...new Set(selectedLabelsForType)];

            let isDisabled = false;

            // Rule 1:
            // Variant must have a label AND that label must be one of the mutual labels
            if (!labelId || !mutualLabels?.includes(labelId)) {
                isDisabled = true;
            }

            // Rule 2:
            // If another duplicate component has already selected a label,
            // enforce that all other duplicates must use the same label
            if (
                uniqueSelected.length > 0 &&
                !uniqueSelected.includes(labelId as number)
            ) {
                isDisabled = true;
            }

            return {
                ...variant,
                isDisabled,
            };
        });
    }, [
        values,
        alternateVariants,
        editorDetails,
        duplicateComponentTypeWithMutualLabel,
    ]);

    if (!component.id) return null;

    return (
        componentType && (
            <VariantBlock>
                <TestFormulaElementField
                    type={component.kind}
                    icon={
                        <ComponentTypesIconWrapper
                            dataKey={componentType?.data_key}
                        />
                    }
                    name={componentType.name}
                    elementId={component.id}
                    data={varinatTableData}
                    selectedElement={selectedVariant}
                />
            </VariantBlock>
        )
    );
};

export default TestFormulaVariantBlock;

const VariantBlock = styled.div({
    display: 'block',
    width: '100%',
});
